import React from 'react'
import { Button, Col, Row } from 'react-bootstrap'

export type LeafNode = { kind: 'leaf'; type: string; parameter: string }
export type TreeNode =
    | { kind: 'all'; children: TreeNode[] }
    | { kind: 'any'; children: TreeNode[] }
    | { kind: 'not'; child: TreeNode }
    | LeafNode

export interface LeafRenderResult {
    typeSelector: React.ReactNode
    parameterInput: React.ReactNode
    isInvalid: boolean
}

/** Validates a tree node, using the caller-supplied leaf validator. */
export function validateTree(node: TreeNode, isLeafValid: (leaf: LeafNode) => boolean): boolean {
    if (node.kind === 'leaf') return isLeafValid(node)
    if (node.kind === 'not') return validateTree(node.child, isLeafValid)
    return node.children.length > 0 && node.children.every(c => validateTree(c, isLeafValid))
}

interface Props {
    node: TreeNode
    onChange: (node: TreeNode) => void
    onRemove?: () => void
    onMoveUp?: () => void
    onMoveDown?: () => void
    /**
     * Renders the leaf-specific type selector and parameter input.
     * The shared component renders the surrounding layout and up/down/remove buttons.
     */
    renderLeaf: (node: LeafNode, onChange: (node: LeafNode) => void, showErrors: boolean) => LeafRenderResult
    defaultLeaf: () => LeafNode
    depth: number
    showErrors: boolean
}

function ConditionTreeEditor({ node, onChange, onRemove, onMoveUp, onMoveDown, renderLeaf, defaultLeaf, depth, showErrors }: Props) {
    if (node.kind === 'leaf') {
        const rendered = renderLeaf(node, n => onChange(n), showErrors)
        return (
            <div className={`cte-node cte-leaf${depth === 0 ? ' condition-tree-editor' : ''}`}>
                <Row className="align-items-center g-2">
                    <Col xs={4}>{rendered.typeSelector}</Col>
                    <Col>
                        {rendered.parameterInput}
                        {rendered.isInvalid && <div className="text-danger small mt-1">Required</div>}
                    </Col>
                    <Col xs="auto" className="d-flex gap-1">
                        <Button size="sm" variant="outline-secondary" disabled={!onMoveUp} onClick={onMoveUp}>↑</Button>
                        <Button size="sm" variant="outline-secondary" disabled={!onMoveDown} onClick={onMoveDown}>↓</Button>
                        {onRemove && <Button size="sm" variant="outline-danger" onClick={onRemove}>✕</Button>}
                    </Col>
                </Row>
            </div>
        )
    }

    if (node.kind === 'not') {
        return (
            <div className={`cte-node cte-composite cte-composite--not${depth === 0 ? ' condition-tree-editor' : ''}`}>
                <div className="d-flex align-items-center gap-2 mb-2">
                    <span className="cte-label">NOT the following</span>
                    <Button size="sm" variant="outline-secondary" disabled={!onMoveUp} onClick={onMoveUp}>↑</Button>
                    <Button size="sm" variant="outline-secondary" disabled={!onMoveDown} onClick={onMoveDown}>↓</Button>
                    {onRemove && <Button size="sm" variant="outline-danger" onClick={onRemove}>✕</Button>}
                </div>
                <div className="cte-children">
                    <ConditionTreeEditor
                        node={node.child}
                        onChange={newChild => onChange({ ...node, child: newChild })}
                        depth={depth + 1}
                        showErrors={showErrors}
                        renderLeaf={renderLeaf}
                        defaultLeaf={defaultLeaf}
                    />
                </div>
            </div>
        )
    }

    const updateChild = (i: number, newChild: TreeNode) =>
        onChange({ ...node, children: node.children.map((c, idx) => idx === i ? newChild : c) })
    const removeChild = (i: number) =>
        onChange({ ...node, children: node.children.filter((_, idx) => idx !== i) })
    const moveChild = (i: number, dir: -1 | 1) => {
        const children = [...node.children]
        ;[children[i], children[i + dir]] = [children[i + dir], children[i]]
        onChange({ ...node, children })
    }
    const addLeaf = () => onChange({ ...node, children: [...node.children, defaultLeaf()] })
    const addGroup = (kind: 'all' | 'any' | 'not') => {
        if (kind === 'not') {
            onChange({ ...node, children: [...node.children, { kind: 'not', child: defaultLeaf() }] })
        } else {
            onChange({ ...node, children: [...node.children, { kind, children: [defaultLeaf()] }] })
        }
    }
    const toggleKind = () => onChange({ ...node, kind: node.kind === 'all' ? 'any' : 'all' })

    const isEmpty = showErrors && node.children.length === 0
    const label = node.kind === 'all' ? 'ALL of the following' : 'ANY of the following'

    return (
        <div className={`cte-node cte-composite cte-composite--${node.kind}${depth === 0 ? ' condition-tree-editor' : ''}`}>
            <div className="d-flex align-items-center gap-2 mb-2">
                <span className="cte-label">{label}</span>
                <Button size="sm" variant="outline-secondary" onClick={toggleKind}>Toggle ALL/ANY</Button>
                <Button size="sm" variant="outline-secondary" disabled={!onMoveUp} onClick={onMoveUp}>↑</Button>
                <Button size="sm" variant="outline-secondary" disabled={!onMoveDown} onClick={onMoveDown}>↓</Button>
                {onRemove && <Button size="sm" variant="outline-danger" onClick={onRemove}>✕</Button>}
            </div>
            {isEmpty && <div className="text-danger small mb-2">Group must have at least one condition</div>}
            <div className="cte-children">
                {node.children.map((child, i) => (
                    <ConditionTreeEditor
                        key={i}
                        node={child}
                        onChange={newChild => updateChild(i, newChild)}
                        onRemove={node.children.length > 1 ? () => removeChild(i) : undefined}
                        onMoveUp={i > 0 ? () => moveChild(i, -1) : undefined}
                        onMoveDown={i < node.children.length - 1 ? () => moveChild(i, 1) : undefined}
                        depth={depth + 1}
                        showErrors={showErrors}
                        renderLeaf={renderLeaf}
                        defaultLeaf={defaultLeaf}
                    />
                ))}
                <div className="d-flex gap-2 mt-2">
                    <Button size="sm" variant="outline-secondary" onClick={addLeaf}>+ Condition</Button>
                    <Button size="sm" variant="outline-secondary" onClick={() => addGroup('all')}>+ ALL group</Button>
                    <Button size="sm" variant="outline-secondary" onClick={() => addGroup('any')}>+ ANY group</Button>
                    <Button size="sm" variant="outline-secondary" onClick={() => addGroup('not')}>+ NOT group</Button>
                </div>
            </div>
        </div>
    )
}

export default ConditionTreeEditor
